import { ReactNode, useState } from 'react'
import { Box, Stack, Typography, Tooltip, IconButton, Divider } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { PROPERTY_VIEW_EMPTY as EMPTY, fmtDate, fmtDateTime, fmtCurrency } from '../utils/propertyFormat'

export type PropertyFieldType = 'text' | 'date' | 'datetime' | 'currency' | 'boolean'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          navigator.clipboard?.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1200)
          })
        }}
        sx={{ p: 0.25 }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 14 }} color="success" />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  )
}

export interface PropertyFieldProps {
  label: string
  value?: string | number | boolean | null
  type?: PropertyFieldType
  currency?: string
  /** Render the value in a monospace font and allow it to wrap on any char (ids, hashes). */
  mono?: boolean
  /** Show a copy-to-clipboard button next to the value. */
  copyable?: boolean
  /** Span the full width of the section grid. */
  full?: boolean
  /** Custom value node (chips, links, ratings). Overrides `value`. */
  children?: ReactNode
}

function renderValue(value: PropertyFieldProps['value'], type?: PropertyFieldType, currency?: string): string {
  if (value == null || value === '') return EMPTY
  switch (type) {
    case 'date':
      return fmtDate(value as string)
    case 'datetime':
      return fmtDateTime(value as string)
    case 'currency':
      return fmtCurrency(value as number, currency)
    case 'boolean':
      return value ? 'Yes' : 'No'
    default:
      return String(value)
  }
}

export function PropertyField({
  label,
  value,
  type,
  currency,
  mono,
  copyable,
  full,
  children,
}: PropertyFieldProps) {
  const hasValue = children != null || (value != null && value !== '')
  return (
    <Box sx={{ gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
      <Typography
        component="div"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontSize: '0.66rem',
          fontWeight: 700,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {children != null ? (
          children
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: mono ? 'monospace' : undefined,
              fontSize: mono ? '0.8rem' : undefined,
              wordBreak: mono ? 'break-all' : 'break-word',
              color: hasValue ? 'text.primary' : 'text.disabled',
            }}
          >
            {renderValue(value, type, currency)}
          </Typography>
        )}
        {copyable && hasValue && value != null && value !== '' && (
          <CopyButton text={String(value)} />
        )}
      </Box>
    </Box>
  )
}

export interface PropertySectionProps {
  title?: string
  /** Optional node rendered on the right of the section title (e.g. a small action). */
  action?: ReactNode
  /** Number of columns on >= sm screens. Defaults to 2. */
  columns?: 1 | 2 | 3
  children: ReactNode
}

export function PropertySection({ title, action, columns = 2, children }: PropertySectionProps) {
  return (
    <Box>
      {(title || action) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          {title && (
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.6 }}
            >
              {title}
            </Typography>
          )}
          {action}
        </Stack>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${columns}, minmax(0, 1fr))`,
          },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export interface PropertyViewProps {
  children: ReactNode
}

/** Consistent, read-only presentation for entity detail views. Wraps one or more
 *  PropertySection blocks, separated by dividers. */
export function PropertyView({ children }: PropertyViewProps) {
  return (
    <Stack spacing={2.5} divider={<Divider flexItem />} sx={{ width: '100%' }}>
      {children}
    </Stack>
  )
}

export default PropertyView
